import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Typography, CircularProgress, Button, Tooltip, IconButton } from "@mui/material";
import { Help } from "@mui/icons-material";
import { min, max } from "lodash";
import { formatDateTime } from "../helpers/time";
import history from "../helpers/history";
import { Transmitter } from "../helpers/glossary";
import { useSelectorTyped as useSelector, useThunkDispatch } from "../redux/common";
import {
  queryDetectionSummary,
  fetchDetectionCountsHistogram,
  queryAbacus,
  clearDetectionData,
  getDetCountApi,
  startDownloadDetections,
} from "../redux/detections/detection-actions";
import { DetectionCountOptions } from "../redux/detections/detection-types";
import { listFilesApi } from "../redux/files/files-actions";
import { readDeploymentList } from "../redux/deployments/deployments-actions";

import { SpinnerInnovasea, ListItemSwitch } from "../fathom-brella";
import ResizableSplitPanel from "../components/common/ResizableSplitPanel";
import DateTimePicker from "../components/common/DateTimePicker";
import FlexCol from "../components/common/FlexCol";
import FlexRow from "../components/common/FlexRow";
import useFileProcessingWarning from "../components/hooks/useFileProcessingWarning";
import HelpPopover from "../components/common/HelpPopover";
import { SubTitle, Title, SoftTitle } from "../components/common/typography";
import { DetectionVisualizer } from "../components/detection/DetectionVisualizer";
import {
  AnimalsDetectionTab,
  isFeatureEnabled,
} from "../components/common/ExperimentalFeaturesDialog";
import { readAnimalList } from "../redux/animals/animals-actions";
import WarningIcon from "../components/common/WarningIcon";
import { DEFAULT_BIN_SECONDS, IDEAL_BINS } from "../components/detection/detection-consts";
import {
  detectionFiltersToTimeRangeTS,
  getBinInfo,
  buildDetectionFilter,
  buildSelectableTxs,
} from "../components/detection/detection-helper";
import {
  RxSelection,
  TxSelection,
  SelectionInterval,
  AbacusGroupMode,
} from "../components/detection/detection-types";
import DetectionDownloadConfirm from "../components/detection/DetectionDownloadConfirm";
import { useStore } from "react-redux";
import { RootState } from "../redux/store";

/** Time range as ISOString */
type TimeRange = {
  min: string;
  max: string;
};

/** reference time for calculating min date. Doesn't matter that it's not exactly "now" */
const nowIsh = new Date().toISOString();

// #region MEMO SELECT STATE
import {
  selectAnimalState,
  selectStudyState,
  selectDeploymentsState,
  selectFilesState,
  selectDetectionSummary,
  selectDetectionHistogram,
  selectDetectionAbacus,
  selectDetectionCount,
  selectAnimalConflicts,
} from "../redux/detections/detection-selectors";
import { Animal } from "../redux/animals/animals-types";
import {
  VirtualizedAutocomplete,
  AutocompleteOption,
} from "../components/common/VirtualizedAutocomplete";
// #endregion

let debounceSetSelInterval: NodeJS.Timeout;
let debounceHistogramFetch: NodeJS.Timeout;
let debounceAbacusFetch: NodeJS.Timeout;
let debounceCountFetch: NodeJS.Timeout;

function Detections() {
  const dispatch = useThunkDispatch();
  const storeState = useStore().getState() as RootState;

  // Feature flag
  const newAnimalFeatEnabled = isFeatureEnabled(AnimalsDetectionTab);

  //#region COMPONENT STATE
  // Detection page level state
  const [filterState, setFilterState] = useState({
    start: "",
    end: "",
    selectedRxs: [] as RxSelection[],
    selectedTxs: [] as TxSelection[],
  });
  const [studyAnimalsOnly, setStudyAnimalsOnly] = useState<boolean>(false);
  const initialTimeRange = useRef<TimeRange>({ min: nowIsh, max: "" });
  const [displayMode, setDisplayMode] = useState<"table" | "abacus">("table");
  const [confirmDLOpen, setConfirmDLOpen] = useState<boolean>(false);

  // Overview plot state - bins and selection made by brushing overview
  const [selectionInterval, setSelectionInterval] = useState([] as SelectionInterval);

  // abacus
  const [abacusGroupBy, setAbacusGroupBy] = useState("RX" as AbacusGroupMode);
  //#endregion
  //#region STORE STATE
  const { studyAnimalsTxIDs } = selectStudyState(storeState);
  const { deploymentsTimeRange } = selectDeploymentsState(storeState);
  const { filesHasHR, filesTimeRange, fileIdToNameMap } = selectFilesState(storeState);
  const { rxs } = selectDetectionSummary(storeState);
  const {
    count: detCount,
    loading: detCountLoading,
    timeCorrectedCount: detCountTC,
  } = selectDetectionCount(storeState); // detection counts in selected filters
  const { detectionHistogram, histogramTimeRange } = selectDetectionHistogram(storeState);
  const { detectionAbacus } = selectDetectionAbacus(storeState);
  const { animalError, animalsByTxID, animals } = selectAnimalState(storeState);
  const { deviceConflictedAnimals } = selectAnimalConflicts(storeState);

  // Having these in useSelector ensures a render occurs after a state update. The memoized
  // selectors track state updates to know when to re-run the selector logic, but do not in
  // themseleves trigger a render.
  const {
    selectedWorkspaceId,
    selectedStudyId,
    summaryLoading,
    summaryError,
    filesLoaded,
    animalLoading,
    studyLoaded,
    deploymentsLoading,
    useTimeCorrection,
  } = useSelector(state => ({
    summaryLoading: state.detection.summary.loading,
    summaryError: state.detection.summary.error,
    filesLoaded: state.files.isLoaded,
    animalLoading: state.animals.loading,
    selectedWorkspaceId: (state.workspaces.selectedWorkspace || ({} as any)).id,
    selectedStudyId: state.study.selectedId,
    studyLoaded: state.study.isLoaded,
    deploymentsLoading: state.deployments.loading,
    useTimeCorrection: state.user.useTimeCorrection,
  }));

  //
  //#endregion
  const zeroDets = detCount === 0;
  const dataSourceReady = filesLoaded && !summaryLoading && !animalLoading;
  const dataSourceError = summaryError || animalError;

  //#region useMemo
  const txs = useMemo(
    () => buildSelectableTxs(rxs, filterState.selectedRxs),
    [rxs, filterState.selectedRxs]
  );

  // DetectionFilterInput WITH NO Brush selection
  // used for filter/fetching overview histogram data
  const filtersNoSelection = useMemo(
    () =>
      buildDetectionFilter({
        filterState,
        rxs,
      }),
    [filterState, rxs]
  );

  // DetectionFilterInput WITH Brush selection
  // used for filter/fetching overview abacus/table data
  const filtersWithSelection = useMemo(
    () =>
      buildDetectionFilter({
        filterState,
        rxs,
        selectionInterval,
      }),
    [filterState, rxs, selectionInterval]
  );

  // Start / end of filters in interval min / max format
  const filterInterval = useMemo(
    () => detectionFiltersToTimeRangeTS(filtersNoSelection),
    [filtersNoSelection]
  );

  // Calculate overviewBinSeconds based on filterInterval
  const overviewBinSeconds = useMemo(() => {
    return filterInterval.max && filterInterval.min
      ? getBinInfo((filterInterval.max - filterInterval.min) / 1000, IDEAL_BINS).getValueSeconds()
      : DEFAULT_BIN_SECONDS;
  }, [filterInterval]);

  //
  //#endregion

  function transmitterLabel(transmitterID: string): string {
    const txIDAnimals: Animal[] | undefined = animalsByTxID.get(transmitterID);
    if (!txIDAnimals) {
      return transmitterID;
    } else if (txIDAnimals.length === 1) {
      return txIDAnimals[0].name ? `${txIDAnimals[0].name} (${transmitterID})` : transmitterID;
    } else {
      return `${txIDAnimals.length} Animals (${transmitterID})`;
    }
  }

  const rxOptions: AutocompleteOption[] = rxs.map(rx => ({
    key: rx.serial,
    label: rx.rxName,
    labelSuffix: (
      <Typography variant="caption" style={{ padding: 5 }}>
        (DETS: {rx.count})
      </Typography>
    ),
  }));

  const txOptions: AutocompleteOption[] = txs.map(tx => ({
    key: tx.displayId,
    label: transmitterLabel(tx.displayId),
    labelSuffix: (
      <Typography variant="caption" style={{ padding: 5 }}>
        (DETS: {tx.count})
      </Typography>
    ),
  }));

  //#region FUNCTIONS
  function resetState() {
    initialTimeRange.current = { min: nowIsh, max: "" };
    setFilterState({
      start: "",
      end: "",
      selectedRxs: [],
      selectedTxs: [],
    });
  }

  // Unified filter change handler
  const filterChange = useCallback(
    (field?: "start" | "end" | "selectedRxs" | "selectedTxs", value?: any) => {
      setFilterState(prev => {
        const updated = { ...prev };
        if (field === "start") {
          updated.start = value;
        } else if (field === "end") {
          updated.end = value;
        } else if (field === "selectedRxs") {
          updated.selectedRxs = rxs.filter(rx => value.includes(rx.serial));
        } else if (field === "selectedTxs") {
          const newTxs = txs.filter(tx => value.includes(tx.displayId));
          updated.selectedTxs = newTxs;
          // keep studyAnimalsOnly in sync
          const selectedTxIds = newTxs.map(t => t.displayId);
          const onlyStudyAnimalsSelected =
            selectedTxIds.length > 0 && selectedTxIds.every(id => studyAnimalsTxIDs.includes(id));
          setStudyAnimalsOnly(onlyStudyAnimalsSelected);
        }
        return updated;
      });

      clearTimeout(debounceSetSelInterval);
      debounceSetSelInterval = setTimeout(() => setSelectionInterval([]), 500);
    },
    [setSelectionInterval, rxs, studyAnimalsTxIDs, txs]
  );

  function downloadDetections() {
    if (newAnimalFeatEnabled && deviceConflictedAnimals.length > 0) {
      setConfirmDLOpen(true);
    } else {
      dispatch(startDownloadDetections(filtersWithSelection, detCount, newAnimalFeatEnabled));
    }
  }

  function haveFiltersSelected() {
    if (filterState.selectedRxs.length > 0) {
      return true;
    }
    if (filterState.selectedTxs.length > 0) {
      return true;
    }
    if (filterState.start != formatDateTime(initialTimeRange.current.min)) {
      return true;
    }
    if (filterState.end != formatDateTime(initialTimeRange.current.max)) {
      return true;
    }
    return false;
  }

  function clearFilters() {
    filterChange("selectedRxs", []);
    filterChange("selectedTxs", []);
    filterChange("start", formatDateTime(initialTimeRange.current.min));
    filterChange("end", formatDateTime(initialTimeRange.current.max));
  }
  //#region useEffect fetching
  // Fetching histogram data (overview plot):
  useEffect(() => {
    clearTimeout(debounceHistogramFetch);
    const histogramDataOptions: DetectionCountOptions = {
      binIntervalSeconds: overviewBinSeconds,
      byReceiverSerial: false,
      byTransmitterIDs: false,
      correctedTime: storeState.user.useTimeCorrection,
    };
    debounceHistogramFetch = setTimeout(() => {
      dispatch(fetchDetectionCountsHistogram(filtersNoSelection, histogramDataOptions));
    }, 250);
  }, [dispatch, filtersNoSelection, overviewBinSeconds, storeState.user.useTimeCorrection]);

  // Update det count on filter change (decoupled detectionSummary to avoid circular dep)
  useEffect(() => {
    clearTimeout(debounceCountFetch);
    debounceCountFetch = setTimeout(() => {
      dispatch(getDetCountApi(filtersWithSelection, useTimeCorrection));
    }, 250);
  }, [dispatch, filtersWithSelection, useTimeCorrection]);

  // Fetching Abacus data:
  useEffect(() => {
    if (displayMode === "abacus" && filterInterval?.min > 0 && filterInterval?.max > 0) {
      const start = filtersWithSelection.includeStartTime?.valueOf() || 0;
      const end = filtersWithSelection.includeEndTime?.valueOf() || 0;
      const timeDifference = end - start;
      if (timeDifference > 0) {
        const binWidth = getBinInfo(timeDifference / 1000);
        const abacusOptions: DetectionCountOptions = {
          byReceiverSerial: abacusGroupBy === "RX",
          byTransmitterIDs: abacusGroupBy === "TX",
          binIntervalSeconds: binWidth.getValueSeconds(),
          correctedTime: storeState.user.useTimeCorrection,
        };
        clearTimeout(debounceAbacusFetch);
        debounceAbacusFetch = setTimeout(() => {
          dispatch(queryAbacus(filtersWithSelection, abacusOptions));
        }, 250);
      }
    }
  }, [
    dispatch,
    displayMode,
    filtersWithSelection,
    filterInterval,
    abacusGroupBy,
    selectionInterval,
    storeState.user.useTimeCorrection,
  ]);

  // changing workspace || study and initital load
  useEffect(() => {
    resetState();
    dispatch(clearDetectionData());
    dispatch(queryDetectionSummary());
    dispatch(listFilesApi());
    dispatch(readDeploymentList());
    dispatch(readAnimalList());
    setStudyAnimalsOnly(false);
  }, [dispatch, selectedWorkspaceId, selectedStudyId]);
  //#endregion

  // Set initial time range:
  useEffect(() => {
    if (filesLoaded && studyLoaded && !deploymentsLoading && !summaryLoading) {
      const _start = min([filesTimeRange.min, deploymentsTimeRange.min]) as string;
      const _end = max([filesTimeRange.max, deploymentsTimeRange.max]) as string;
      const initStart = initialTimeRange.current.min;
      const initEnd = initialTimeRange.current.max;
      // End will be populated when files and / or deployments are known. Until then it is ""
      // Don't go setting state until a useful value can be populated
      if (_start != initStart && _end != initEnd) {
        initialTimeRange.current = { min: _start, max: _end };
        setFilterState(prev => ({
          ...prev,
          start: formatDateTime(_start),
          end: formatDateTime(_end),
        }));
        setSelectionInterval([]);
      }
    }
  }, [
    filesTimeRange,
    deploymentsTimeRange,
    deploymentsLoading,
    filesLoaded,
    studyLoaded,
    summaryLoading,
  ]);

  //#endregion

  useFileProcessingWarning();

  // escape hatches:
  if (!dataSourceReady) {
    return <SpinnerInnovasea text={"Preparing detection database"} />;
  }

  if (dataSourceError) {
    history.push("/error");
    return null;
  }

  // We don't specify a txs filter when querying for all txs,
  // but we will need to specify the full list when displaying
  // it in the abacus plot
  const columnFilter = {
    ...filtersWithSelection,
    includeTransmitterIDs: filtersWithSelection.includeTransmitterIDs
      ? filtersWithSelection.includeTransmitterIDs
      : txs.map(tx => tx.displayId),
  };

  return (
    <>
      <DetectionDownloadConfirm
        open={confirmDLOpen}
        onCancel={() => setConfirmDLOpen(false)}
        onDownload={() => {
          dispatch(startDownloadDetections(filtersWithSelection, detCount, newAnimalFeatEnabled));
          setConfirmDLOpen(false);
        }}
      />
      <ResizableSplitPanel
        direction="horizontal"
        firstInit="25%"
        firstMin={300}
        firstContent={
          <FlexCol fullWidth fullHeight paddingLevel={2} autoScrollY>
            <FlexRow>
              <Title style={{ paddingRight: 24 }}>Filters</Title>
              {haveFiltersSelected() && (
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear All
                </Button>
              )}
            </FlexRow>
            <FlexCol paddingLevel={1}>
              <SubTitle>Devices</SubTitle>
              <FlexCol itemSpacing={3} paddingLevel={1.5}>
                <VirtualizedAutocomplete
                  options={rxOptions}
                  selection={filterState.selectedRxs.map(({ rxName, serial }) => ({
                    key: serial,
                    label: rxName,
                  }))}
                  selectionHandler={selection =>
                    filterChange(
                      "selectedRxs",
                      selection.map(s => s.key)
                    )
                  }
                  label={`Search for receivers in ${(selectedStudyId && "study") || "workspace"}`}
                  placeholder="Type to filter and/or select from below"
                />
                <VirtualizedAutocomplete
                  options={txOptions}
                  selection={filterState.selectedTxs.map(({ displayId }) => ({
                    key: displayId,
                    label: transmitterLabel(displayId),
                  }))}
                  selectionHandler={selection =>
                    filterChange(
                      "selectedTxs",
                      selection.map(s => s.key)
                    )
                  }
                  label={`Search for ${Transmitter.pluralText}`}
                  placeholder="Type to filter and/or select from below"
                />
              </FlexCol>
              <FlexRow vAlign="center">
                <ListItemSwitch
                  text="Only study animals"
                  checked={studyAnimalsOnly}
                  tooltip={{
                    text: selectedStudyId
                      ? studyAnimalsTxIDs.length == 0
                        ? "There are no animals tagged in the currently selected study."
                        : "Automatically select all animals in the currently selected study."
                      : `Select a study to limit ${Transmitter.pluralText} to animals in that study.`,
                    placement: "bottom",
                  }}
                  onChange={() =>
                    studyAnimalsOnly
                      ? filterChange("selectedTxs", [])
                      : filterChange("selectedTxs", studyAnimalsTxIDs)
                  }
                  disabled={!selectedStudyId || studyAnimalsTxIDs.length == 0}
                  size="small"
                />
                {filesHasHR && (
                  <FlexRow vAlign="center" hAlign="right">
                    <Typography variant="caption" noWrap>
                      HR Detections
                    </Typography>
                    <HelpPopover
                      tooltip="Where are my HR detections?"
                      helpContent={
                        "Support for working with detection data from HR2 and HR3 receivers is under development"
                      }
                    />
                  </FlexRow>
                )}
              </FlexRow>
            </FlexCol>
            <FlexCol paddingLevel={1}>
              <SubTitle>Time Frame</SubTitle>
              <FlexCol itemSpacing={3} paddingLevel={1.5}>
                <DateTimePicker
                  label="Start time"
                  value={filterState.start}
                  onChange={(value: string) => filterChange("start", value)}
                  helperText={"Limit to detections after this time"}
                />
                <DateTimePicker
                  label="End time"
                  value={filterState.end}
                  onChange={(value: string) => filterChange("end", value)}
                  helperText={"Limit to detections before this time"}
                />
              </FlexCol>
            </FlexCol>
            <FlexCol hAlign="center">
              {detCountLoading || summaryLoading ? (
                <CircularProgress size={18} />
              ) : (
                <>
                  <SubTitle style={{ margin: 8 }}>
                    {`${detCount.toLocaleString()} detections in selected filters`}
                  </SubTitle>
                  {useTimeCorrection &&
                    (detCount - detCountTC === 0 ? (
                      <SoftTitle style={{ margin: 8 }}>
                        All detections have been time-corrected
                      </SoftTitle>
                    ) : (
                      <SoftTitle style={{ margin: 8 }}>
                        {`${(detCount - detCountTC).toLocaleString()} detections were not able to be time-corrected`}
                      </SoftTitle>
                    ))}
                </>
              )}
              <FlexRow spaceBetween itemSpacing={1}>
                <Tooltip
                  title="By default, all data is selected prior to any filtering."
                  placement="top"
                >
                  <IconButton size="small">
                    <Help />
                  </IconButton>
                </Tooltip>
                {zeroDets && !detCountLoading && (
                  <WarningIcon
                    tooltip={`This tool is driven by study associations. Please ensure you have linked files and/or deployments to the study you wish to analyze.`}
                  />
                )}
              </FlexRow>
            </FlexCol>
          </FlexCol>
        }
        secondContent={
          <DetectionVisualizer
            useTimeCorrection={useTimeCorrection}
            newAnimalFeatEnabled={newAnimalFeatEnabled}
            columnFilters={columnFilter}
            overviewBinSeconds={overviewBinSeconds}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            detectionCount={detCount}
            downloadDetections={downloadDetections}
            histogram={detectionHistogram}
            abacus={detectionAbacus}
            filterInterval={filterInterval}
            histogramTimeRange={histogramTimeRange}
            selectionInterval={selectionInterval}
            setSelectionInterval={setSelectionInterval}
            abacusGroupBy={abacusGroupBy}
            setAbacusGroupBy={setAbacusGroupBy}
            animals={animals}
            fileIdToNameMap={fileIdToNameMap}
          />
        }
      />
    </>
  );
}

export default Detections;
